import { get } from 'svelte/store';

import { apiClient } from '$lib/api/client';
import { turnstile } from '$lib/turnstile';
import { queryFilters, scoreFilterRange } from '$lib/stores/selection';
import { getDefaultFilters, submitFilters } from '$lib/api/filters';
import { submitNeighborSamples } from '$lib/api/neighbors';
import { selectedIndustryCentroids } from '$lib/stores/data';
import { neighborSamples } from '$lib/stores/neighbors';
import { applicationMode, type ApplicationMode } from '$lib/stores/mode';
import type { Filters } from '$lib/types/api';

/**
 * Handing a selection to somebody else.
 *
 * # What travels is the search, not its results
 *
 * A selection is a description — filters, industries, examples — and the
 * companies it matches are a consequence of it, recomputed in under a second.
 * The corpus grows every night, so a frozen list would be a snapshot the
 * recipient cannot tell is stale. A link re-runs.
 *
 * # The code is the whole of the authorisation
 *
 * Whoever holds it can open the selection, which is what makes a link a link.
 * It carries eighty bits of randomness, so it cannot be found by trying.
 */

interface SharedSelection {
  mode: ApplicationMode;
  filters: Filters;
  industries: { id: string; name: string }[];
  samples: { id: string; name: string }[];
  score_range?: [number, number];
}

/** The query parameter a shared link arrives under. */
export const shareParam = 'share';

/**
 * Strip the customer's own list out of what is shared.
 *
 * The exclusion code is a capability: whoever holds it can read every row of
 * the list it names, which is the sender's customer base. A link is sent to
 * somebody else — often outside the company — so the code must not ride along.
 * The recipient gets the search without the subtraction, which is the only
 * thing that can be shown to somebody who does not have the list.
 */
function withoutTheCustomerList(filters: Filters): Filters {
  return { ...filters, exclude_list_code: '' };
}

/**
 * Fill in what a stored selection does not carry.
 *
 * A share holds the filters of the day it was made. Every filter added since is
 * missing from it, and arrives as `null` or not at all — `exclude_legal_form`
 * did, for every share stored before 2026-09-03. The stores are written
 * straight from this object and have no defaults of their own: a store holding
 * null where the panel expects a list is a crash on `.length`, and the
 * recipient of a link watches a spinner that never stops.
 *
 * So the payload is laid over a full set of defaults rather than trusted to be
 * one. The nested objects are merged a level down because that is where filters
 * get added — `structured_filters` has taken four new fields this year.
 *
 * The API normalises its half of this too (see SelectionFilters.WithEmptyListsForNil),
 * which is what fixes the links already sent. This is the half that survives the
 * next field being added.
 */
function withDefaults(filters: Filters): Filters {
  const defaults = getDefaultFilters();

  // Spreading a key whose value is null keeps the null — `{...{a: null}}` is
  // not `{}` — so a present-but-null field has to be replaced rather than
  // defaulted over. `??` does both: it catches the missing key and the null.
  const fill = <T>(given: T | null | undefined, fallback: T): T => given ?? fallback;

  return {
    ...defaults,
    ...filters,

    postal_radius: {
      ...defaults.postal_radius,
      ...fill(filters.postal_radius, defaults.postal_radius)
    },
    signal_filters: {
      ...defaults.signal_filters,
      ...fill(filters.signal_filters, defaults.signal_filters)
    },

    postal_codes: fill(filters.postal_codes, defaults.postal_codes),
    cantons: fill(filters.cantons, defaults.cantons),
    languages: fill(filters.languages, defaults.languages),
    contact_roles: fill(filters.contact_roles, defaults.contact_roles),
    contact_titles: fill(filters.contact_titles, defaults.contact_titles),
    text_constraints: fill(filters.text_constraints, defaults.text_constraints),

    structured_filters: {
      ...defaults.structured_filters,
      ...fill(filters.structured_filters, defaults.structured_filters),
      legal_form: fill(
        filters.structured_filters?.legal_form,
        defaults.structured_filters.legal_form
      ),
      exclude_legal_form: fill(
        filters.structured_filters?.exclude_legal_form,
        defaults.structured_filters.exclude_legal_form
      )
    }
  };
}

/** Store the selection on screen and return the code that reads it back. */
export async function createShare(): Promise<string> {
  let turnstileToken: string | undefined;
  try {
    turnstileToken = await Promise.race([
      turnstile.getToken(),
      new Promise<string>((_, reject) =>
        setTimeout(() => reject(new Error('Turnstile timeout')), 5000)
      )
    ]);
  } catch {
    // Continue without one; the service decides whether it was required.
  }

  const response = await apiClient.post<{ code: string }>('/deep-iq/share', {
    mode: get(applicationMode),
    filters: withoutTheCustomerList(get(queryFilters)),
    industries: Array.from(get(selectedIndustryCentroids), ([id, name]) => ({ id, name })),
    samples: get(neighborSamples).map((sample) => ({ id: sample.id, name: sample.name ?? '' })),
    // The match-score band is not a filter — the search returns what it
    // returns and the list hides the rows outside it — but it decides what the
    // sender is looking at, and what an order would carry. A link without it
    // hands over a different set from the one on screen.
    score_range: get(scoreFilterRange),
    turnstile_token: turnstileToken
  });

  return response.data.code;
}

/**
 * Put a shared selection on screen.
 *
 * Writes the same stores the guide and the filter panel write, so what the
 * recipient gets is an ordinary selection they can edit, not a read-only view
 * of somebody else's. Returns false when the code names nothing — a mistyped
 * link should say so rather than silently leaving the screen as it was.
 */
export async function applyShare(code: string): Promise<SharedSelection | null> {
  let shared: SharedSelection;
  try {
    const response = await apiClient.get<SharedSelection>(
      `/deep-iq/share/${encodeURIComponent(code)}`
    );
    shared = response.data;
  } catch {
    return null;
  }

  if (!shared?.filters) return null;

  // Stripped on the way out too, in case an older link carries one: a code
  // from somebody else's browser would name a list this customer cannot see,
  // and every search would be refused for it.
  queryFilters.set(withDefaults(withoutTheCustomerList(shared.filters)));
  selectedIndustryCentroids.set(
    new Map((shared.industries ?? []).map((industry) => [industry.id, industry.name]))
  );
  // A sample is only ever read for its identifier and its name — the search
  // sends the first and the panel shows the second — so the two fields that
  // travelled are the whole of what a restored sample needs.
  neighborSamples.set(
    (shared.samples ?? []).map((sample) => ({ id: sample.id, name: sample.name }))
  );

  if (shared.mode) applicationMode.set(shared.mode);

  return shared;
}

/**
 * Open a shared link: restore the selection, run its search, and narrow to the
 * band the sender was looking at.
 *
 * The order is the point. Running a search resets the match-score filter to its
 * full range — deliberately, because a band from a previous search means
 * nothing against new results — so restoring the band before the search would
 * be undone by it, silently, and the recipient would see rows the sender had
 * filtered away.
 */
export async function openShare(code: string): Promise<boolean> {
  const shared = await applyShare(code);
  if (!shared) return false;

  if (shared.mode === 'neighbor') {
    await submitNeighborSamples();
  } else {
    await submitFilters();
  }

  const band = shared.score_range;
  if (Array.isArray(band) && band.length === 2) {
    scoreFilterRange.set([band[0], band[1]]);
  }

  return true;
}

/** The link to send, for a code. */
export function shareLink(code: string): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set(shareParam, code);
  return url.toString();
}
